import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { combineClasses } from '@okee-uikit/react-utils';
import { useRefCallback, useUpdate } from '@okee-uikit/react-hooks';
import { useDirection, useLocale, usePrefixCls } from '@okee-uikit/react-config';
import { Search } from '../../../common/icons';
import { useSelect } from '../../../hooks/select';
import { useGetTrigger } from '../../../hooks/popper';
import { Empty } from '../../empty';
import { Input } from '../../input';
export function SelectPopoverPanel(props) {
  var localePlaceholder = useLocale('select.search.placeholder');
  var show = props.show,
      _props$searchable = props.searchable,
      searchable = _props$searchable === void 0 ? false : _props$searchable,
      className = props.className,
      _props$autoAlign = props.autoAlign,
      autoAlign = _props$autoAlign === void 0 ? true : _props$autoAlign,
      autoFocusSearch = props.autoFocusSearch,
      _props$popoverWidth = props.popoverWidth,
      popoverWidth = _props$popoverWidth === void 0 ? 110 : _props$popoverWidth,
      triggerId = props.triggerId,
      children = props.children,
      emptyContent = props.emptyContent,
      emptyRender = props.emptyRender,
      onSearchChange = props.onSearchChange,
      _props$searchPlacehol = props.searchPlaceholder,
      searchPlaceholder = _props$searchPlacehol === void 0 ? localePlaceholder : _props$searchPlacehol,
      renderFooter = props.renderFooter;
  var direction = useDirection();
  var select = useSelect();
  var update = useUpdate();
  var panelRef = useRef(null);
  var inputRef = useRef(null);
  var queryChange = useRefCallback(function (query) {
    update();
    onSearchChange(query);
  });
  var activeTrigger = useGetTrigger(triggerId);
  var panelWidth = useMemo(function () {
    return autoAlign && activeTrigger ? activeTrigger.getRect().width : popoverWidth;
  }, [show, autoAlign, activeTrigger, popoverWidth]);
  var extraProps = select.getExtraProps();
  var size = extraProps.size || 'md';
  var showOptions = select.getShowOptionsIdSort();
  var classes = combineClasses(usePrefixCls('select-popover-panel', {
    size: size,
    search: searchable,
    empty: !showOptions.length,
    rtl: direction === 'rtl'
  }), className);
  var selectInnerCls = usePrefixCls('select-popover-panel-inner');
  var searchCls = usePrefixCls('select-search-input');
  var localeEmpty = useLocale('select.empty');
  var handleSearch = useCallback(function (event) {
    select.setQuery(event.target.value);
  }, [select]);
  useEffect(function () {
    if (select.searchQuery) queryChange(select.searchQuery);
  }, []);
  useEffect(function () {
    var panel = panelRef.current;

    if (!panel) {
      return;
    }

    var options = [].slice.call(panel.querySelectorAll('[data-type=select-option]')).sort(function (a, b) {
      var code = a.compareDocumentPosition(b);
      return code & 8 ? -1 : code & 16 ? 1 : code & 4 ? -1 : code & 2 ? 1 : 0;
    });
    var ids = options.map(function (item) {
      return item.getAttribute('data-option-id') || '';
    }).filter(function (id) {
      return !!id;
    });
    select.updateOptionIdSort(ids);
  });
  useEffect(function () {
    if (autoFocusSearch && show) {
      setTimeout(function () {
        var _a, _b;

        (_b = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus) === null || _b === void 0 ? void 0 : _b.call(_a);
      }, 0);
    }
  }, [show, autoFocusSearch]);
  useEffect(function () {
    select.addListener('query-change', queryChange);
    return function () {
      select.removeListener('query-change', queryChange);
    };
  }, []);
  var onKeyDown = useCallback(function (e) {
    var _a, _b, _c;

    var keyCode = e.keyCode;

    if (keyCode === 40) {
      (_a = select.stepDown) === null || _a === void 0 ? void 0 : _a.call(select);
    } else if (keyCode === 38) {
      (_b = select.stepUp) === null || _b === void 0 ? void 0 : _b.call(select);
    } else if (keyCode === 13) {
      (_c = select.selectBackFill) === null || _c === void 0 ? void 0 : _c.call(select);
    }
  }, []);

  var renderSearch = function renderSearch() {
    if (!searchable) {
      return null;
    }

    return React.createElement("div", {
      className: searchCls
    }, React.createElement(Input, {
      onKeyDown: onKeyDown,
      inputRef: inputRef,
      value: select.searchQuery,
      onChange: handleSearch,
      prefix: React.createElement(Search, null),
      placeholder: searchPlaceholder,
      clearable: true,
      outlined: false
    }));
  };

  var renderEmpty = function renderEmpty() {
    if (showOptions.length) {
      return null;
    }

    if (emptyRender) {
      return typeof emptyRender === 'function' ? emptyRender(!!select.searchQuery) : emptyRender;
    }

    var content = emptyContent ? typeof emptyContent === 'function' ? emptyContent(!!select.searchQuery) : emptyContent : localeEmpty;
    return React.createElement(Empty, {
      content: content
    });
  };

  return React.createElement("div", {
    className: classes,
    style: {
      width: panelWidth
    },
    ref: panelRef
  }, renderSearch(), React.createElement("div", {
    className: selectInnerCls
  }, children, renderEmpty()), !!renderFooter && renderFooter());
}